import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { isAlive, PopularLazyLoadSelector } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import ReportsSchedulesTableRowComponent from 'webapp/components/reports/schedules/table-row';
import TableSorterComponent from 'webapp/components/table-sorter';

import type { AddTagAction, DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortReportSchedule from 'webapp/models-custom/filter-sort/report-schedule';
import type Report from 'webapp/models-custom/report';
import type ReportSchedule from 'webapp/models-custom/report-schedule';

type Args = Record<string, never>;

type Action = AddTagAction | DeleteAction;

export default class ReportsSchedulesTableComponent extends Component<Args> {
	@service('timer') declare timerService: Service.Timer;
	@service('model-manager/report-schedules')
	declare mmReportSchedulesService: Service.ModelManager.ReportSchedules;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	filterSort: FilterSortReportSchedule;
	batchSelect: BatchSelect<ReportSchedule>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortReportSchedule({
			selectors: PopularLazyLoadSelector.table,
			getAllItems: () => {
				return this.model;
			},
		});

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as Action);
			},
		});
	}

	private _model: ReportSchedule[] = [];

	@cached
	get model(): ReportSchedule[] {
		this.mmReportSchedulesService.updateModelCache(
			this._model,
			this.mmReportSchedulesService.unsentSavedRecords,
		);
		return this._model;
	}

	get thresholdTimestamp(): number {
		return this.timerService.currentTs + 7 * 24 * 60 * 60;
	}

	get renderFirstTable(): boolean {
		return this.filterSort.results.some(
			(schedule) => schedule.scheduledOn < this.thresholdTimestamp,
		);
	}

	get renderSecondTable(): boolean {
		return this.filterSort.results.some(
			(schedule) => schedule.scheduledOn >= this.thresholdTimestamp,
		);
	}

	@cached
	get hideFirstTable(): boolean {
		const scheduledInNext7 = this.scheduledInNext7FilterFn(this.filterSort.results);
		return !this.filterSort.results.some((schedule) => {
			return scheduledInNext7.includes(schedule);
		});
	}

	@cached
	get hideSecondTable(): boolean {
		const scheduledLater = this.scheduledLaterFilterFn(this.filterSort.results);
		return !this.filterSort.results.some((schedule) => {
			return scheduledLater.includes(schedule);
		});
	}

	get batchButtonContainerNode() {
		return isAlive(this) && this.selectedItems.length
			? this.navigationService.batchButtonContainerNode
			: null;
	}

	get selectedItems(): ReportSchedule[] {
		return this.batchSelect.selectedItems;
	}

	get batchOptions(): PopupListItem<Action>[] {
		const batchSelect = this.batchSelect;
		return [batchSelect.addTagsOption, batchSelect.deleteOption];
	}

	scheduledInNext7FilterFn = (items: ReportSchedule[]) => {
		const thresholdTs = this.thresholdTimestamp;
		return items.filter((schedule) => schedule.scheduledOn < thresholdTs);
	};

	scheduledLaterFilterFn = (items: ReportSchedule[]) => {
		const thresholdTs = this.thresholdTimestamp;
		return items.filter((schedule) => schedule.scheduledOn >= thresholdTs);
	};

	doBatchAction(actionName: Action) {
		return this[actionName]();
	}

	@action
	batchDelete() {
		return this.mmReportSchedulesService.batchDelete(this.selectedItems);
	}

	@action
	batchUpdateTags() {
		const reports: Report[] = [];

		this.selectedItems.forEach((schedule) => {
			const report = schedule.report;
			if (report && !reports.find((existingReport) => existingReport.id === report.id)) {
				reports.push(report);
			}
		});

		return this.mmReportsService.batchUpdateTags(reports);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} />

		<div id="reports-schedules-list" class="bottom-padding">
			{{! Scheduled for upcoming 7 days section }}
			{{#if this.renderFirstTable}}
				<CommonListLazyLoadComponent
					@filterSort={{this.filterSort}}
					@filterExtraFn={{this.scheduledInNext7FilterFn}}
					@initialRowCount={{10}}
					@loadMoreRowCount={{5}}
					@animationProperty="id"
					@autoLoaded={{true}}
					@bottomDistance={{700}}
					as |visibleItems exporter|
				>
					<table
						class="flexible-table bigtable has-checkbox has-avatar-big
							{{if this.hideFirstTable 'hide'}}"
						{{visibleCheckContainer}}
						{{didUpdate this.triggerContentChange visibleItems}}
					>
						<colgroup>
							<col class="cc" />
							<col class="h0" />
							<col width="25%" />
							<col width="20%" />
							<col />
							<col />
							<col class="button-ct" />
						</colgroup>
						<thead>
							<tr>
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
								<th colspan="2">
									<strong>Scheduled for upcoming 7 days</strong>
								</th>
								<th class="prio-low">Schedule</th>
								<th class="prio-high">
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="scheduledOn"
										@name="Scheduled time"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th class="prio-low">Owner</th>
								<th class="prio-low">Tags</th>
								<th class="button-ct"></th>
							</tr>
						</thead>
						<tbody>
							{{#each visibleItems as |item|}}
								<ReportsSchedulesTableRowComponent
									@model={{item}}
									@batchSelect={{this.batchSelect}}
								/>
							{{/each}}
						</tbody>
					</table>
					{{#unless exporter.areAllItemsLoaded}}
						<CommonListLoaderComponent />
					{{/unless}}
				</CommonListLazyLoadComponent>
			{{/if}}

			{{! Scheduled for later section }}
			{{#if this.renderSecondTable}}
				<CommonListLazyLoadComponent
					@filterSort={{this.filterSort}}
					@filterExtraFn={{this.scheduledLaterFilterFn}}
					@initialRowCount={{10}}
					@loadMoreRowCount={{5}}
					@animationProperty="id"
					@autoLoaded={{true}}
					@bottomDistance={{700}}
					as |visibleItems exporter|
				>
					<table
						class="flexible-table bigtable has-checkbox has-avatar-big
							{{if this.hideSecondTable 'hide'}}"
						{{visibleCheckContainer}}
						{{didUpdate this.triggerContentChange visibleItems}}
					>
						<colgroup>
							<col class="cc" />
							<col class="h0" />
							<col width="25%" />
							<col width="20%" />
							<col />
							<col />
							<col class="button-ct" />
						</colgroup>
						<thead>
							<tr>
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
								<th colspan="2">
									<strong>Scheduled for later</strong>
								</th>
								<th class="prio-low">Scheduled frequency</th>
								<th>
									<TableSorterComponent
										@selectedProp={{this.filterSort.sortProp}}
										@selectedOrder={{this.filterSort.sortOrder}}
										@prop="scheduledOn"
										@name="Scheduled time"
										@changeProp={{this.filterSort.changeSortProp}}
										@changeOrder={{this.filterSort.changeSortOrder}}
									/>
								</th>
								<th class="prio-low">Owner</th>
								<th class="prio-low">Tags</th>
								<th class="button-ct"></th>
							</tr>
						</thead>
						<tbody>
							{{#each visibleItems as |item|}}
								<ReportsSchedulesTableRowComponent
									@model={{item}}
									@batchSelect={{this.batchSelect}}
								/>
							{{/each}}
						</tbody>
					</table>
					{{#unless exporter.areAllItemsLoaded}}
						<CommonListLoaderComponent />
					{{/unless}}
				</CommonListLazyLoadComponent>
			{{/if}}
		</div>

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
